import { Client } from "@modelcontextprotocol/sdk/client/index.js";
import { CallToolResult, GetPromptResult, Prompt, ReadResourceResult, Resource, Tool } from "@modelcontextprotocol/sdk/types.js";
import { StreamableHTTPClientTransport } from "@modelcontextprotocol/sdk/client/streamableHttp.js";
import { getAuthToken, getUserEmail } from "services/AccountServices";
import { circuitBreaker } from "circuitbreaker/CircuitBreaker";
import { mcpConfigService, MCPServerConfig } from "mcp/services/MCPConfigService";
import { sessionManager } from "utility/SessionManager";

export interface MCPClientState {
    isCindraConnected: boolean;
    isConnecting: boolean;
    error: string | null;
    tools: Tool[];
    resources: Resource[];
    prompts: Prompt[];
    lastConnectionAttempt?: Date;
    reconnectAttempts: number;
    isCindraEnabled: boolean;
}

// Tools that don't require the auth_token argument
const TOOLS_WITHOUT_TOKEN = new Set([
    "server_status"
]);

export class MCPClientService {
    private client: Client | null = null;
    private transport: StreamableHTTPClientTransport | null = null;
    private listeners: Set<(state: MCPClientState) => void> = new Set();
    private currentConfig: MCPServerConfig | null = null;
    private reconnectInterval: ReturnType<typeof setInterval> | null = null;
    private healthCheckInterval: ReturnType<typeof setInterval> | null = null;
    private reconnectDelayMs = 5000; // 5 seconds
    private healthCheckDelayMs = 10000; // 10 seconds
    private isReconnecting = false;
    private healthCheckInProgress = false; // Prevent duplicate health checks

    private state: MCPClientState = {
        isCindraConnected: false,
        isConnecting: false,
        error: null,
        tools: [],
        resources: [],
        prompts: [],
        reconnectAttempts: 0,
        isCindraEnabled: this.isCindraEnabled()
    };

    constructor() {
        if (this.isCindraEnabled()) {
            this.startHealthCheck();

            // Subscribe to session state changes
            sessionManager.subscribe((isActive) => {
                if (!isActive) {
                    console.log("[MCPClientService] Session inactive - pausing MCP operations");
                    this.pauseMCPOperations();
                } else {
                    console.log("[MCPClientService] Session active - resuming MCP operations");
                }
            });
        }
        else {
            console.log("[MCPClientService] Cindra is disabled via VITE_CINDRA_ENABLED environment variable");
        }
    }

    private pauseMCPOperations() {
        console.log("[MCPClientService] Pausing all MCP operations due to session timeout/logout");

        // Stop health checks
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
            this.healthCheckInterval = null;
            console.log("[MCPClientService] Stopped health check interval");
        }

        // Stop reconnection attempts
        this.stopReconnectionAttempts();
    }

    // ---=== Private Methods ===--- //

    private isCindraEnabled(): boolean {
        // In React/Vite, environment variables are available via import.meta.env and prefixed with VITE_
        const cindraEnabled = import.meta.env.VITE_CINDRA_ENABLED;
        return cindraEnabled === "true";
    }

    private getDefaultConfig(): MCPServerConfig | null {
        if (!this.isCindraEnabled()) {
            return null;
        }

        try {
            const configs = mcpConfigService.getServerConfigs();
            if (configs.length > 0) {
                const defaultConfig = configs[0]; // Use first config as default
                console.log(`[MCPClientService] Using default MCP server: ${defaultConfig.name} at ${defaultConfig.url}`);
                return defaultConfig;
            }
            else {
                console.log("[MCPClientService] No MCP server configurations available");
                return null;
            }
        }
        catch (error) {
            console.error("[MCPClientService] Error loading default MCP config:", error);
            return null;
        }
    }

    private async loadCapabilities(): Promise<void> {
        if (!this.client) {
            console.warn("[MCPClientService] Cannot load capabilities - client not initialized");
            return;
        }

        try {
            console.log("[MCPClientService] Loading MCP server capabilities");

            // Load tools
            const toolsResponse = await this.client.listTools();
            const tools = toolsResponse.tools || [];
            console.log(`[MCPClientService] Loaded ${tools.length} tools`);

            // Load resources
            const resourcesResponse = await this.client.listResources();
            const resources = resourcesResponse.resources || [];
            console.log(`[MCPClientService] Loaded ${resources.length} resources`);

            // Load prompts
            const promptsResponse = await this.client.listPrompts();
            const prompts = promptsResponse.prompts || [];
            console.log(`[MCPClientService] Loaded ${prompts.length} prompts`);

            this.updateState({ tools, resources, prompts });
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to load capabilities";
            console.error("[MCPClientService] Failed to load capabilities:", errorMessage, error);
            this.updateState({ error: errorMessage });
        }
    }

    private convertArgsToStrings(args?: Record<string, unknown>): Record<string, string> {
        if (!args) return {};

        const stringArgs: Record<string, string> = {};
        for (const [key, value] of Object.entries(args)) {
            if (typeof value === "string") {
                stringArgs[key] = value;
            }
            else {
                // Convert non-string values to JSON strings
                stringArgs[key] = JSON.stringify(value);
            }
        }
        return stringArgs;
    }

    private notifyListeners() {
        this.listeners.forEach(listener => listener(this.state));
    }

    private updateState(updates: Partial<MCPClientState>) {
        this.state = { ...this.state, ...updates };
        this.notifyListeners();
    }

    private startHealthCheck() {
        // Clear existing health check
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
        }

        this.healthCheckInterval = setInterval(async () => {
            // Skip health check if session is inactive or health check already in progress
            if (!sessionManager.isActive()) {
                console.log("[MCPClientService] Skipping health check - session inactive");
                return;
            }

            if (this.healthCheckInProgress) {
                console.log("[MCPClientService] Skipping health check - already in progress");
                return;
            }

            if (this.state.isCindraConnected && !this.state.isConnecting && this.isCindraEnabled()) {
                this.healthCheckInProgress = true;
                try {
                    // Try to call a simple tool to check if the server is still responsive
                    await this.callTool("server_status");
                }
                catch (error) {
                    console.warn("[MCPClientService] Server status check failed, server may be disconnected:", error);
                    await this.handleDisconnection(error instanceof Error ? error.message : "Server status check failed");
                }
                finally {
                    this.healthCheckInProgress = false;
                }
            }
        }, this.healthCheckDelayMs);
    }

    private async handleDisconnection(errorMessage: string) {
        console.error("[MCPClientService] Handling disconnection:", errorMessage);

        // Update state to reflect disconnection
        this.updateState({
            isCindraConnected: false,
            error: errorMessage,
            tools: [],
            resources: [],
            prompts: []
        });

        // Clean up current connection
        await this.cleanupConnection();

        // Start reconnection attempts if we have a config to reconnect to
        if (this.currentConfig) {
            console.log(`[MCPClientService] Starting reconnection attempts to ${this.currentConfig.name}`);
            this.startReconnectionAttempts();
        } else {
            console.warn("[MCPClientService] No config available for reconnection");
        }
    }

    private startReconnectionAttempts() {
        if (this.reconnectInterval || this.isReconnecting) {
            console.log("[MCPClientService] Reconnection already in progress");
            return; // Already attempting reconnection
        }

        console.log("[MCPClientService] Starting unlimited reconnection attempts");
        this.isReconnecting = true;

        this.reconnectInterval = setInterval(async () => {
            // Skip reconnection if session is inactive
            if (!sessionManager.isActive()) {
                console.log("[MCPClientService] Skipping reconnection attempt - session inactive");
                return;
            }

            if (!this.state.isConnecting && !this.state.isCindraConnected && this.currentConfig) {
                const attemptNumber = this.state.reconnectAttempts + 1;
                console.log(`[MCPClientService] Reconnection attempt #${attemptNumber} to ${this.currentConfig.name}`);

                this.updateState({
                    reconnectAttempts: attemptNumber,
                    lastConnectionAttempt: new Date()
                });

                try {
                    await this.internalConnect(this.currentConfig, true);

                    if (this.state.isCindraConnected) {
                        console.log(`[MCPClientService] Reconnection successful after ${attemptNumber} attempt(s)`);

                        // Signal circuit breaker that connectivity has been restored
                        // Note: We don't have access to NotificationManager here, but the CircuitBreaker
                        // will use the global notification manager when recordSuccess is called from API calls
                        console.log("[MCPClientService] Signaling circuit breaker that connectivity has been restored");
                        circuitBreaker.recordSuccess();

                        this.stopReconnectionAttempts();
                        this.updateState({ reconnectAttempts: 0 });
                    }
                }
                catch (reconnectError) {
                    const errorMsg = reconnectError instanceof Error ? reconnectError.message : "Unknown error";
                    console.warn(`[MCPClientService] Reconnection attempt #${attemptNumber} failed:`, errorMsg);
                }
            }
        }, this.reconnectDelayMs);
    }

    private stopReconnectionAttempts() {
        if (this.reconnectInterval) {
            console.log("[MCPClientService] Stopping reconnection attempts");
            clearInterval(this.reconnectInterval);
            this.reconnectInterval = null;
        }
        this.isReconnecting = false;
    }

    private async internalConnect(config: MCPServerConfig, isReconnect: boolean = false): Promise<void> {
        if (!this.isCindraEnabled()) {
            throw new Error("MCPClientService is disabled. Set VITE_CINDRA_ENABLED=true to enable.");
        }

        if (this.state.isConnecting) {
            console.log("[MCPClientService] Connection already in progress");
            return;
        }

        // If we're already connected to the same server, don't reconnect
        if (this.state.isCindraConnected && this.currentConfig?.url === config.url && !isReconnect) {
            console.log(`[MCPClientService] Already connected to ${config.name}`);
            return;
        }

        const connectionType = isReconnect ? "Reconnecting" : "Connecting";
        console.log(`[MCPClientService] ${connectionType} to ${config.name} at ${config.url}`);

        try {
            this.updateState({
                isConnecting: true,
                error: null,
                lastConnectionAttempt: new Date()
            });

            // Clean up any existing connection
            await this.cleanupConnection();

            // Store current config for reconnection attempts
            this.currentConfig = config;

            // Create transport with required headers
            const userEmail = getUserEmail();
            const headers: Record<string, string> = {
                "X-User-Agent": "KarunaPORTAL (MCPClientService)"
            };

            // Add X-User-Email header if user email is available
            if (userEmail) {
                headers["X-User-Email"] = userEmail;
            } else {
                console.warn("[MCPClientService] User email not available - X-User-Email header will not be included");
            }

            this.transport = new StreamableHTTPClientTransport(
                new URL(config.url),
                {
                    requestInit: {
                        headers
                    }
                }
            );

            // Create client
            this.client = new Client({
                name: "karuna-mcp-client",
                version: "0.0.1"
            }, {
                capabilities: {
                    tools: {},
                    resources: {},
                    prompts: {}
                }
            });

            // Connect client to transport
            await this.client.connect(this.transport);
            console.log(`[MCPClientService] Transport connected successfully to ${config.name}`);

            // Load available tools, resources, and prompts
            await this.loadCapabilities();

            this.updateState({
                isCindraConnected: true,
                isConnecting: false,
                error: null
            });

            console.log(`[MCPClientService] Successfully connected to ${config.name}`);

            // If this is a reconnection, signal circuit breaker that connectivity has been restored
            if (isReconnect) {
                console.log("[MCPClientService] Reconnection established - signaling circuit breaker");
                circuitBreaker.recordSuccess();
            }

        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            console.error(`[MCPClientService] Connection failed to ${config.name} at ${config.url}:`, errorMessage, error);

            this.updateState({
                isCindraConnected: false,
                isConnecting: false,
                error: errorMessage
            });

            // Clean up failed connection
            await this.cleanupConnection();
            throw error;
        }
    }

    private async cleanupConnection(): Promise<void> {
        if (this.client) {
            try {
                await this.client.close();
            }
            catch (error) {
                console.warn("[MCPClient] Error closing client:", error);
            }
            this.client = null;
        }

        if (this.transport) {
            try {
                await this.transport.close();
            }
            catch (error) {
                console.warn("[MCPClient] Error closing transport:", error);
            }
            this.transport = null;
        }
    }

    // ---=== Public API ===--- //

    public isEnabled(): boolean {
        return this.isCindraEnabled();
    }

    public getState(): MCPClientState {
        return this.state;
    }

    public subscribe(listener: (state: MCPClientState) => void): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    public async connect(config?: MCPServerConfig): Promise<void> {
        if (!this.isCindraEnabled()) {
            console.log("[MCPClientService] Connect called but Cindra is disabled");
            return; // Gracefully return without throwing
        }

        // Stop any ongoing reconnection attempts when manually connecting
        this.stopReconnectionAttempts();
        this.updateState({ reconnectAttempts: 0 });

        // Use provided config or load default
        let configToUse = config;
        if (!configToUse) {
            configToUse = this.getDefaultConfig();
            if (!configToUse) {
                throw new Error("No MCP server configuration available and none provided");
            }
        }

        console.log(`[MCPClientService] Attempting to connect to MCP server: ${configToUse.name} at ${configToUse.url}`);

        try {
            // Use the existing internalConnect method
            await this.internalConnect(configToUse, false);
            console.log(`[MCPClientService] Connected to MCP server: ${configToUse.name} at ${configToUse.url}`);
        }
        catch (error) {
            console.error(`[MCPClientService] Initial connection failed: ${error}`);

            // Store the config for reconnection attempts even if initial connection fails
            this.currentConfig = configToUse;

            // Start reconnection attempts for failed initial connection
            this.startReconnectionAttempts();

            // Don't throw the error - let the reconnection process handle it
            console.log("[MCPClientService] Starting unlimited reconnection attempts after initial connection failure");
        }
    }

    public async forceReconnect(): Promise<void> {
        if (!this.isCindraEnabled()) {
            console.log("[MCPClientService] Force reconnect called but Cindra is disabled");
            return; // Gracefully return without throwing
        }

        if (this.currentConfig) {
            console.log("[MCPClient] Force reconnect initiated");

            // Stop current reconnection attempts
            this.stopReconnectionAttempts();
            this.updateState({ reconnectAttempts: 0 });

            try {
                await this.internalConnect(this.currentConfig, true);
                console.log("[MCPClient] Force reconnect successful");
            }
            catch (error) {
                console.warn("[MCPClient] Force reconnect failed, restarting retry cycle:", error);
                // Restart reconnection attempts if force reconnect fails
                if (this.currentConfig) {
                    this.startReconnectionAttempts();
                }
                throw error;
            }
        }
    }

    public async disconnect(): Promise<void> {
        if (!this.isCindraEnabled()) {
            console.log("[MCPClientService] Disconnect called but Cindra is disabled");
            return; // Gracefully return without throwing
        }

        try {
            // Stop reconnection attempts
            this.stopReconnectionAttempts();

            // Clear current config so we don't try to reconnect
            this.currentConfig = null;

            await this.cleanupConnection();

            this.updateState({
                isCindraConnected: false,
                isConnecting: false,
                error: null,
                tools: [],
                resources: [],
                prompts: [],
                reconnectAttempts: 0
            });
        }
        catch (error) {
            this.updateState({
                error: error instanceof Error ? error.message : "Error during disconnect"
            });
        }
    }

    public async callTool(name: string, arguments_?: any): Promise<CallToolResult> {
        if (!this.isCindraEnabled()) {
            throw new Error("MCPClientService is disabled. Set VITE_CINDRA_ENABLED=true to enable.");
        }

        // Check session state before making call
        if (!sessionManager.isActive()) {
            const error = new Error("Session is inactive - cannot make MCP call");
            console.error(`[MCPClientService] Tool call '${name}' blocked - session inactive`);
            throw error;
        }

        if (!this.client || !this.state.isCindraConnected) {
            const error = new Error("MCP client is not connected");
            console.error(`[MCPClientService] Tool call '${name}' failed - client not connected`);
            throw error;
        }

        console.log(`[MCPClientService] Calling tool: ${name}`);

        try {
            let finalArguments = arguments_ || {};

            // Add auth_token as first argument for tools that require it
            if (!TOOLS_WITHOUT_TOKEN.has(name)) {
                const auth_token = getAuthToken();
                if (!auth_token) {
                    const error = new Error("No authentication token available for tool call");
                    console.error(`[MCPClientService] Tool call '${name}' failed - no auth token`);
                    throw error;
                }

                // Create new arguments object with auth_token as first argument
                finalArguments = {
                    auth_token: auth_token,
                    ...finalArguments
                };
            }

            const result = await this.client.callTool({
                name,
                arguments: finalArguments
            });

            console.log(`[MCPClientService] Tool call '${name}' completed successfully`);
            return result;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            console.error(`[MCPClientService] Tool call '${name}' failed:`, errorMessage, error);

            // Check if this looks like a connection error
            if (errorMessage.includes("fetch") || errorMessage.includes("network") || errorMessage.includes("ECONNREFUSED") || errorMessage.includes("Failed to fetch")) {
                console.warn(`[MCPClientService] Tool call '${name}' failed due to connection error, triggering disconnection handler`);
                await this.handleDisconnection(errorMessage);
            }

            throw error;
        }
    }

    public async readResource(uri: string): Promise<ReadResourceResult> {
        if (!this.isCindraEnabled()) {
            throw new Error("MCPClientService is disabled. Set VITE_CINDRA_ENABLED=true to enable.");
        }

        // Check session state before making call
        if (!sessionManager.isActive()) {
            const error = new Error("Session is inactive - cannot make MCP call");
            console.error(`[MCPClientService] Read resource '${uri}' blocked - session inactive`);
            throw error;
        }

        if (!this.client || !this.state.isCindraConnected) {
            const error = new Error("MCP client is not connected");
            console.error(`[MCPClientService] Read resource '${uri}' failed - client not connected`);
            throw error;
        }

        console.log(`[MCPClientService] Reading resource: ${uri}`);

        try {
            const result = await this.client.readResource({ uri });
            console.log(`[MCPClientService] Resource '${uri}' read successfully`);
            return result;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Resource read failed";
            console.error(`[MCPClientService] Read resource '${uri}' failed:`, errorMessage, error);

            // Check if this looks like a connection error
            if (errorMessage.includes("fetch") || errorMessage.includes("network") || errorMessage.includes("ECONNREFUSED") || errorMessage.includes("Failed to fetch")) {
                console.warn(`[MCPClientService] Read resource '${uri}' failed due to connection error, triggering disconnection handler`);
                await this.handleDisconnection(errorMessage);
            }
            else {
                this.updateState({ error: errorMessage });
            }

            throw new Error(errorMessage);
        }
    }

    public async getPrompt(name: string, args?: Record<string, unknown>): Promise<GetPromptResult> {
        if (!this.isCindraEnabled()) {
            throw new Error("MCPClientService is disabled. Set VITE_CINDRA_ENABLED=true to enable.");
        }

        // Check session state before making call
        if (!sessionManager.isActive()) {
            const error = new Error("Session is inactive - cannot make MCP call");
            console.error(`[MCPClientService] Get prompt '${name}' blocked - session inactive`);
            throw error;
        }

        if (!this.client || !this.state.isCindraConnected) {
            const error = new Error("MCP client is not connected");
            console.error(`[MCPClientService] Get prompt '${name}' failed - client not connected`);
            throw error;
        }

        console.log(`[MCPClientService] Getting prompt: ${name}`);

        try {
            const result = await this.client.getPrompt({
                name,
                arguments: this.convertArgsToStrings(args)
            });
            console.log(`[MCPClientService] Prompt '${name}' retrieved successfully`);
            return result;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Prompt get failed";
            console.error(`[MCPClientService] Get prompt '${name}' failed:`, errorMessage, error);

            // Check if this looks like a connection error
            if (errorMessage.includes("fetch") || errorMessage.includes("network") || errorMessage.includes("ECONNREFUSED") || errorMessage.includes("Failed to fetch")) {
                console.warn(`[MCPClientService] Get prompt '${name}' failed due to connection error, triggering disconnection handler`);
                await this.handleDisconnection(errorMessage);
            }
            else {
                this.updateState({ error: errorMessage });
            }

            throw new Error(errorMessage);
        }
    }

    // Cleanup method
    public destroy(): void {
        this.stopReconnectionAttempts();
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
            this.healthCheckInterval = null;
        }
        if (this.isCindraEnabled()) {
            void this.disconnect();
        }
    }
}

// Singleton instance
export const mcpClientService = new MCPClientService();
